import { ReactElement } from 'react';
import { Anchor } from '@twilio-paste/core/anchor';
import { Box } from '@twilio-paste/core/box';
import { Button } from '@twilio-paste/core/button';
import { CodeBlock } from '@twilio-paste/core/code-block';
import { Heading } from '@twilio-paste/core/heading';
import { ListItem, OrderedList, UnorderedList } from '@twilio-paste/core/list';
import { Paragraph } from '@twilio-paste/core/paragraph';
import {
  Table,
  THead,
  Tr,
  Th,
  TBody,
  TFoot,
  Td,
} from '@twilio-paste/core/table';
import { Text } from '@twilio-paste/core/text';
import RouterAnchor from '@twiliointernal/services-common/components/routerAnchor';

import {
  composeAttributesWithoutStyle,
  convertCodeElementsToString,
  convertClassNameToSnippetLanguage,
  isMultiLineCodeBlock,
} from '.';
import { constants, types } from '../definitions';
import {
  isArticleAttachmentUrl,
  isKnowledgeBaseUrl,
  getKnowledgeBaseUrlRelativePath,
} from '../../../utils';
import { trackingDetails } from '../../../definitions/constants';
import { logger } from '@twiliointernal/org/twilio/e2-commons/shared/libs';

import styles from '../htmlPasteParser.module.css';

/**
 * Maps a HTML tag to its corresponding Paste component
 *  - e.g. tag `a` will render <Anchor />
 *  - Return type has "false" explicitly included to adhere how `html-react-parser` works.
 *    When returning "false", it means that we will not be replacing the DOM Node with any Paste components.
 *    For example, tags such as `<strong>` or non-element node such as plain text should be rendered as is
 *  - Disclaimer: At the time of writing, our only use case is handling tags(html string) provided by ZenDesk API.
 *    Though this function was written to be as generic as possible, there still may exist issues once it starts
 *    handling html string from another data source.
 */
export const renderPasteComponentByHtmlTag = ({
  attributes,
  childElements,
  htmlTag,
  parentElement,
  trackEvent,
}: types.RenderPasteComponentByHtmlTagParams): ReactElement | false => {
  const composedAttributes = composeAttributesWithoutStyle(attributes);
  const { href } = composedAttributes;

  const pasteComponentsByHtmlTag: {
    [key: string]: () => ReactElement;
  } = {
    a: () => {
      const isKnowledgeBaseLink = href && isKnowledgeBaseUrl(href);
      const isArticleAttachmentLink = href && isArticleAttachmentUrl(href);
      // Point article attachment links to ZD Guide
      if (isArticleAttachmentLink) {
        const externalLink = getKnowledgeBaseUrlRelativePath(href);

        return (
          <Anchor href={externalLink} target="_blank" rel="noreferrer">
            {childElements}
          </Anchor>
        );
      }
      // Point internal links to OSC instead of ZD Support
      if (isKnowledgeBaseLink) {
        const internalLink = getKnowledgeBaseUrlRelativePath(href);
        composedAttributes.href = internalLink;

        return (
          <RouterAnchor
            to={internalLink}
            {...composedAttributes}
            trackEvent={(): void => {
              const eventName = trackingDetails.articleInternalLinkClicked;
              logger.info(`${constants.LOG_PREFIX}::[${eventName}]`, {
                link: internalLink,
              });
              trackEvent(eventName, {
                link: internalLink,
              });
            }}
          >
            {childElements}
          </RouterAnchor>
        );
      }

      return (
        <Anchor href="#" {...composedAttributes}>
          {childElements}
        </Anchor>
      );
    },

    article: () => (
      <Box {...composedAttributes} as="article">
        {childElements}
      </Box>
    ),
    button: () => <Button variant="secondary">{childElements}</Button>,
    br: () => <Box as="br" />,
    code: () => {
      if (isMultiLineCodeBlock(parentElement)) {
        const className = attributes?.class || '';
        const language = convertClassNameToSnippetLanguage(className);
        const code = convertCodeElementsToString(childElements);

        return (
          <Box className={styles.componentContainer}>
            <CodeBlock language={language} code={code as string} />
          </Box>
        );
      }

      return <Text as="code">{childElements}</Text>;
    },
    h1: () => (
      <Heading {...composedAttributes} as="h2" variant="heading10">
        {childElements}
      </Heading>
    ),
    h2: () => (
      <Heading {...composedAttributes} as="h2" variant="heading20">
        {childElements}
      </Heading>
    ),
    h3: () => (
      <Heading {...composedAttributes} as="h3" variant="heading30">
        {childElements}
      </Heading>
    ),
    img: () => <Box {...composedAttributes} as="img" maxWidth="100%" />,
    li: () => <ListItem {...composedAttributes}>{childElements}</ListItem>,
    ul: () => (
      <UnorderedList {...composedAttributes}>{childElements}</UnorderedList>
    ),
    ol: () => (
      <OrderedList {...composedAttributes}>{childElements}</OrderedList>
    ),
    main: () => <Box as="main">{childElements}</Box>,
    p: () => <Paragraph {...composedAttributes}>{childElements}</Paragraph>,
    pre: () => (
      <Text as="pre" overflow="auto">
        {childElements}
      </Text>
    ),
    section: () => (
      <Box {...composedAttributes} as="section">
        {childElements}
      </Box>
    ),
    table: () => (
      <Box className={styles.componentContainer}>
        <Table {...composedAttributes} scrollHorizontally>
          {childElements as HTMLTableElement}
        </Table>
      </Box>
    ),
    thead: () => (
      <THead {...composedAttributes}>
        {childElements as HTMLTableSectionElement}
      </THead>
    ),
    tbody: () => (
      <TBody {...composedAttributes}>
        {childElements as HTMLTableSectionElement}
      </TBody>
    ),
    tfoot: () => (
      <TFoot {...composedAttributes}>
        {childElements as HTMLTableSectionElement}
      </TFoot>
    ),
    th: () => (
      <Th {...composedAttributes}>{childElements as HTMLTableRowElement}</Th>
    ),
    td: () => <Td {...composedAttributes}>{childElements}</Td>,
    tr: () => <Tr {...composedAttributes}>{childElements}</Tr>,
    // Add more here to map HTML tags to Paste component
  };

  const composeComponent = pasteComponentsByHtmlTag[htmlTag];

  if (!composeComponent) {
    return false;
  }

  return composeComponent();
};
